class CardProductMappedProductsSwitcher extends HTMLElement {
  constructor() {
    super();

    this.swatches = this.querySelectorAll('.card-product-mapped-products__swatch');
    this.productCard = this.closest('.card-wrapper');
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', this.handleSwatchClick.bind(this));
    });
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    if (swatch.disabled) return;

    this.swatches.forEach(swatch => {
      swatch.classList.remove('is-selected');
      swatch.disabled = false;
    });

    swatch.classList.add('is-selected');
    swatch.disabled = true;

    this.updateProductCardInfo(swatch);
  }

  updateProductCardInfo(swatch) {
    if (!this.productCard) return;

    // Update title href if provided
    const productUrl = swatch.getAttribute('data-product-url');
    if (productUrl) {
      // Update ALL product card links, not just the first one
      const titleLinks = this.productCard.querySelectorAll('.product-card-link, .quick-add__link');
      titleLinks.forEach(link => {
        link.href = productUrl;
      });
    }

    // Update quick add modal opener data-product-url
    const quickAddModalOpener = this.productCard.querySelector('.quick-add modal-opener button');
    if (quickAddModalOpener) {
      quickAddModalOpener.setAttribute('data-product-url', productUrl);
    }

    this.updateImages(swatch);
    this.updatePrice(swatch);
    this.updateVariantOptions(swatch);
    this.updateCustomField(swatch);
    this.updateAvailability(swatch);
  }

  updateVariantOptions(swatch) {
    if (!this.productCard) return;

    const quickAddVariants = this.productCard.querySelector('quick-add-variants');
    if (!quickAddVariants) return;
    const variantOptions = quickAddVariants.querySelector('.variant-options');
    if (!variantOptions) return;

    quickAddVariants.setAttribute('data-product-id', swatch.getAttribute('data-product-id'));
    quickAddVariants.setAttribute('data-product-url', swatch.getAttribute('data-product-url'));

    const raw = swatch.getAttribute('data-variants');
    if (!raw) return;

    let variants;
    try {
      variants = JSON.parse(raw);
    } catch (err) {
      try {
        variants = JSON.parse(raw.replace(/&quot;/g, '"'));
      } catch (e) {
        return;
      }
    }

    if (!Array.isArray(variants) || variants.length === 0) return;

    const productUrl = swatch.getAttribute('data-product-url');

    const html = variants
      .map(variant => {
        const option = document.createElement('button');
        option.classList.add('variant-option');
        option.classList.add(`available-${variant.available}`);

        // Extract the number from the variant title
        const numberMatch = variant.title.match(/(\d+(\.\d+)?)/);

        const optionValue = document.createElement('span');
        optionValue.classList.add('variant-option-value');
        optionValue.textContent = numberMatch ? numberMatch[0] : variant.title;
        option.appendChild(optionValue);


        if (!variant.available) {
          const svgHtml = `
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-mail">
              <path
                d="M0.833252 8.33335V1.66669H9.16658V8.33335H0.833252ZM4.99992 5.41669L1.66659 3.33335V7.50002H8.33325V3.33335L4.99992 5.41669ZM4.99992 4.58335L8.33325 2.50002H1.66659L4.99992 4.58335ZM1.66659 3.33335V2.50002V7.50002V3.33335Z"
                fill="currentColor" />
            </svg>
          `;

          option.insertAdjacentHTML('beforeend', svgHtml);

          option.setAttribute('data-join-waitlist', '');
          option.setAttribute('data-klaviyo-bis-open', '');
          if (productUrl) {
            const bisUrl = `${window.location.origin}${productUrl}?variant=${variant.id}`;
            option.setAttribute('data-klaviyo-bis-url', bisUrl);
          }
        }

        option.dataset.variantId = variant.id;

        return option.outerHTML;
      })
      .join('');

    variantOptions.innerHTML = html;
  }

  updateImages(swatch) {
    // Primary image
    const featuredImg = this.productCard.querySelector('.card__media .media img');
    if (featuredImg) {
      const src = swatch.getAttribute('data-featured-src');
      const srcset = swatch.getAttribute('data-featured-srcset');
      const alt = swatch.getAttribute('data-featured-alt');
      const width = swatch.getAttribute('data-featured-width');
      const height = swatch.getAttribute('data-featured-height');
      if (src) featuredImg.src = src;
      if (srcset) featuredImg.srcset = srcset;
      if (alt) featuredImg.alt = alt;
      if (width) featuredImg.width = parseInt(width, 10);
      if (height) featuredImg.height = parseInt(height, 10);
    }

    // Secondary image (if present in DOM)
    const images = this.productCard.querySelectorAll('.card__media .media img');
    const secondaryImg = images.length > 1 ? images[1] : null;
    if (secondaryImg) {
      const sSrc = swatch.getAttribute('data-secondary-src');
      const sSrcset = swatch.getAttribute('data-secondary-srcset');
      const sAlt = swatch.getAttribute('data-secondary-alt');
      const sWidth = swatch.getAttribute('data-secondary-width');
      const sHeight = swatch.getAttribute('data-secondary-height');
      if (sSrc) secondaryImg.src = sSrc;
      if (sSrcset) secondaryImg.srcset = sSrcset;
      if (sAlt) secondaryImg.alt = sAlt;
      if (sWidth) secondaryImg.width = parseInt(sWidth, 10);
      if (sHeight) secondaryImg.height = parseInt(sHeight, 10);
    }
  }

  updatePrice(swatch) {
    const priceElement = this.productCard.querySelector('.price');
    if (!priceElement) return;

    const priceCents = parseInt(swatch.getAttribute('data-price') || '0', 10);
    const compareAtCents = parseInt(swatch.getAttribute('data-compare-at-price') || '0', 10);

    const priceRegular = priceElement.querySelectorAll('.price-item--regular');
    const priceSale = priceElement.querySelectorAll('.price-item--sale');

    if (compareAtCents > priceCents) {
      priceElement.classList.add('price--on-sale');
      priceSale.forEach(sale => {
        sale.textContent = this.formatMoney(priceCents);
      });
      priceRegular.forEach(regular => {
        regular.textContent = this.formatMoney(compareAtCents);
      });
    } else {
      priceElement.classList.remove('price--on-sale');
      priceRegular.forEach(regular => {
        regular.textContent = this.formatMoney(priceCents);
      });
      priceSale.forEach(sale => {
        sale.textContent = '';
      });
    }
  }

  updateCustomField(swatch) {
    const customFieldWrapper = this.productCard.querySelector('[data-product-custom-field-wrapper]');
    if (!customFieldWrapper) return;

    const customField = swatch.getAttribute('data-swatch-label');

    if (customField) {
      customFieldWrapper.textContent = "in " + customField;
    }
  }

  updateAvailability(swatch) {
    const cardBadge = this.productCard.querySelector('.card__badge');
    if (!cardBadge) return;

    const availability = swatch.getAttribute('data-available');
    const isAvailable = availability !== 'false';
    const sectionId = this.getAttribute('data-section-id');
    const productId = swatch.getAttribute('data-product-id');
    const customBadge = swatch.getAttribute('data-custom-badge');
    const priceCents = parseInt(swatch.getAttribute('data-price') || '0', 10);
    const compareAtCents = parseInt(swatch.getAttribute('data-compare-at-price') || '0', 10);

    const soldOutBadgeColorScheme = this.getAttribute('data-sold-out-badge-color-scheme');
    const saleBadgeColorScheme = this.getAttribute('data-sale-badge-color-scheme');
    const customBadgeColorScheme = this.getAttribute('data-custom-badge-color-scheme');
    const saleBadgeCopyType = this.getAttribute('data-sale-badge-copy-type');

    cardBadge.querySelector('.badge--type-unavailable')?.remove();
    cardBadge.querySelector('.badge--type-custom')?.remove();
    cardBadge.querySelector('.badge--type-sale')?.remove();

    if (!isAvailable) {
      const soldOutTemplate = `
        <span
          id="Badge-${sectionId}-${productId}"
          class="badge badge--bottom-left badge--type-unavailable color-${soldOutBadgeColorScheme}"
        >
          ${window.variantStrings.soldOut}
        </span>
      `;
      cardBadge.insertAdjacentHTML('beforeend', soldOutTemplate);
    } else if (customBadge) {
      const customBadgeTemplate = `
        <span
          id="Badge-${sectionId}-${productId}"
          class="badge badge--bottom-left badge--type-custom color-${customBadgeColorScheme}"
        >
          ${this.escapeHtml(customBadge)}
        </span>
      `;
      cardBadge.insertAdjacentHTML('beforeend', customBadgeTemplate);
    } else if (compareAtCents > priceCents) {
      let saleBadgeText = window.variantStrings?.onSale || 'Sale';
      
      if (saleBadgeCopyType === 'on_sale_save_x') {
        const savings = this.formatMoney(compareAtCents - priceCents);
        saleBadgeText = `${window.variantStrings?.onSaleSaveX || 'Save'} ${savings}`;
      } else if (saleBadgeCopyType === 'on_sale_x_off') {
        const percentOff = Math.round(((compareAtCents - priceCents) * 100) / compareAtCents);
        saleBadgeText = `${percentOff}% ${window.variantStrings?.onSaleXOff || 'Off'}`;
      }
      
      const saleBadgeTemplate = `
        <span
          id="Badge-${sectionId}-${productId}"
          class="badge badge--bottom-left badge--type-sale color-${saleBadgeColorScheme}"
        >
          ${saleBadgeText}
        </span>
      `;
      cardBadge.insertAdjacentHTML('beforeend', saleBadgeTemplate);
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available, otherwise fallback
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return "$" + (cents / 100).toFixed(0);
  }


  escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

customElements.define('card-product-mapped-products-switcher', CardProductMappedProductsSwitcher);